import React, { ReactNode } from "react";
import Page from "../../page-layout";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import { BackButton } from "@core/components";
import { useTranslation } from "react-i18next";
import { useConfig } from "../../../providers/config";

/* Reusable Section Component */

interface DocumentSectionProps {
  id: string;
  title: string;
  children: ReactNode;
}

const DocumentSection = ({ id, title, children }: DocumentSectionProps) => {
  return (
    <section id={id} className="mb-10 scroll-mt-24">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
      <div className="text-gray-700 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
};

const OffertaPage = () => {
  const config = useConfig();
  const { t } = useTranslation();

  const appUrl = config.appUrl;
  const appUrlWithoutHttp = config.appUrl?.replace("https://", "");

  return (
    <Page>
      <Page.Header className="pt-[env(safe-area-inset-top)]">
        <MaxWidthContainer classList="flex items-center justify-between bg-white h-16 pl-2.5 relative">
          <BackButton buttonStyles="justify-self-start ml-0" />
          <h1 className="text-lg font-bold text-center absolute top-5 left-1/2 -translate-x-1/2">
            {t("Offerta")}
          </h1>
        </MaxWidthContainer>
      </Page.Header>

      <Page.Content>
        <MaxWidthContainer>
          <div className="bg-white rounded-2xl shadow-lg border p-8 lg:p-12">
            {/* Header */}
            <header className="text-center mb-10">
              <h2 className="font-bold text-gray-900">
                Публичная оферта о заключении договора на использование мобильного приложения «Parhato»
              </h2>
            </header>

            {/* Company Block */}
            <section className="bg-gray-50 rounded-xl p-6 border mb-10">
              <h2 className="font-semibold text-gray-900 mb-3">Реквизиты компании</h2>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>
                  <b>{config.orgName}</b>
                </li>
                <li>ОГРН: {config.ogrn}</li>
                <li>ИНН/КПП: {config.innKpp}</li>
                <li>Юридический адрес: {config.legalAddress}</li>
                <li>Генеральный директор: {config.director}</li>
                <li>
                  Сайт:
                  <a href={appUrl} className="text-blue-600 hover:underline">
                    {" "}
                    {appUrlWithoutHttp}
                  </a>
                </li>
              </ul>
            </section>

            {/* Sections */}
            <DocumentSection id="general" title="1. Общие положения">
              <ul className="pl-6 space-y-3">
                <li>
                  1.1 Настоящая Оферта является официальным предложением ООО «Пархато» любому физическому лицу заключить
                  договор на условиях, изложенных ниже.
                </li>
                <li>
                  1.2 Факт регистрации и использования мобильного приложения «Parhato» является полным и безоговорочным
                  акцептом условий Оферты.
                </li>
                <li>1.3 Оферта считается заключённой с момента акцепта Пользователем.</li>
              </ul>
            </DocumentSection>

            <DocumentSection id="subject" title="2. Предмет договора">
              <ul className="pl-6 space-y-3">
                <li>
                  2.1 Компания предоставляет Пользователю доступ к функциональности мобильного приложения «Parhato»,
                  предназначенного для заказа и доставки продуктов питания и иных товаров, представленных в приложении.
                </li>
              </ul>
            </DocumentSection>

            <DocumentSection id="rights" title="3. Права и обязанности сторон">
              <ul className="pl-6 space-y-3">
                <li>
                  3.1 Пользователь обязуется предоставлять достоверную информацию при регистрации, использовать
                  Приложение в рамках действующего законодательства РФ, не предпринимать действий, нарушающих работу
                  Приложения.
                </li>
                <li>
                  3.2 Компания обязуется обеспечивать корректную работу Приложения и своевременную доставку заказов.
                </li>
              </ul>
            </DocumentSection>

            <DocumentSection id="price" title="4. Стоимость услуг">
              <ul className="pl-6 space-y-3">
                <li>4.1 Услуги по использованию Приложения предоставляются безвозмездно.</li>
                <li>4.2 Стоимость товаров и доставки определяется в Приложении и подлежит оплате Пользователем.</li>
              </ul>
            </DocumentSection>

            <DocumentSection id="responsibility" title="5. Ответственность сторон">
              <ul className="pl-6 space-y-3">
                <li>
                  5.1 Стороны несут ответственность за неисполнение или ненадлежащее исполнение условий Оферты в
                  соответствии с законодательством РФ.
                </li>
                <li>
                  5.2 Компания не несёт ответственности за перебои в работе Приложения по независящим от неё причинам.
                </li>
              </ul>
            </DocumentSection>

            <DocumentSection id="data" title="6. Персональные данные">
              <ul className="pl-6 space-y-3">
                <li>
                  6.1 Пользователь соглашается на обработку своих персональных данных в соответствии с Федеральным
                  законом № 152-ФЗ.
                </li>
                <li>
                  6.2 Политика конфиденциальности размещена на сайте
                  <a href={appUrl} className="text-blue-600 hover:underline">
                    {" "}
                    {appUrlWithoutHttp}
                  </a>
                </li>
              </ul>
            </DocumentSection>

            <DocumentSection id="other" title="7. Прочие условия">
              <ul className="pl-6 space-y-3">
                <li>
                  7.1 Компания вправе изменять условия Оферты с уведомлением Пользователя посредством публикации новой
                  редакции на сайте.
                </li>
                <li>7.2 Все споры разрешаются в соответствии с законодательством РФ.</li>
              </ul>
            </DocumentSection>

            {/* Footer */}
            <footer className="mt-12 pt-6 border-t text-sm text-gray-500 text-center">
              Документ опубликован в рамках публичной оферты приложения «Parhato»
            </footer>
          </div>
        </MaxWidthContainer>
      </Page.Content>
    </Page>
  );
};

export default OffertaPage;
